import { PersonAdd } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  createUsersBusinessesPortfolio,
  createUsersRelations,
  getCompanyUsers,
} from 'lib/api';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { User } from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ExecutivesResponse } from '../index';
import { Executive } from './ExecutiveRow';

const AsignExecutive = ({
  open,
  onClose,
  businessIdentifier,
  executives,
  refetchExecutives,
}: {
  open: boolean;
  onClose: () => void;
  businessIdentifier: string;
  executives: ExecutivesResponse;
  refetchExecutives: () => Promise<void>;
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | undefined>(
    undefined,
  );
  const [selectedRole, setSelectedRole] = useState<'commercial' | 'collection'>(
    'commercial',
  );

  const isValid = useMemo(() => {
    return selectedUser && selectedRole;
  }, [selectedUser, selectedRole]);
  const operationalExecutives = useMemo(
    () =>
      executives.commercialExecutives.map((executive: Executive) =>
        Number(executive.id),
      ),
    [executives],
  );
  const collectionExecutives = useMemo(
    () =>
      executives.collectionExecutives.map((executive: Executive) =>
        Number(executive.id),
      ),
    [executives],
  );

  const filteredUsers = useMemo(() => {
    if (selectedRole === 'commercial') {
      return users.filter(
        (user: User) => !operationalExecutives.includes(user.id),
      );
    } else {
      return users.filter(
        (user: User) => !collectionExecutives.includes(user.id),
      );
    }
  }, [users, operationalExecutives, collectionExecutives, selectedRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getCompanyUsers(dispatch);
      setUsers(response);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (!selectedUser) {
        showAlert(dispatch, 'error', 'You must select an executive');
        return;
      }
      const data = {
        id: selectedUser,
        businessId: businessIdentifier,
      };
      if (selectedRole === 'commercial') {
        await createUsersRelations(dispatch, data);
      } else {
        await createUsersBusinessesPortfolio(dispatch, selectedUser, {
          businessIdentifiers: [businessIdentifier],
        });
      }
      await refetchExecutives();
      showAlert(dispatch, 'success', 'Ejecutivo asignado correctamente');
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog fullWidth open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle>
        <Title
          title="Asignar ejecutivo"
          icon={<PersonAdd color="primary" />}
          color="primary"
        />
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="dense">
          <InputLabel id="user-label">Ejecutivos disponibles</InputLabel>
          <Select
            labelId="user-label"
            id="user-select"
            value={selectedUser}
            label="Ejecutivos disponibles"
            onChange={(e) => setSelectedUser(Number(e.target.value))}
          >
            {filteredUsers.map((user: User, index: number) => (
              <MenuItem key={index} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel id="role-label">Cargo</InputLabel>
          <Select
            labelId="role-label"
            id="role-select"
            value={selectedRole}
            label="Cargo"
            onChange={(e) =>
              setSelectedRole(e.target.value as 'commercial' | 'collection')
            }
          >
            {['commercial', 'colle  ction'].map(
              (role: string, index: number) => (
                <MenuItem key={index} value={role}>
                  {role === 'commercial' ? 'Comercial' : 'Cobranza'}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <CustomButton color="primary" disabled={!isValid} onClick={handleSave}>
          Guardar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AsignExecutive;
